/* eslint-disable no-console */
/**
 * Dev-only event queue for manual socket event control.
 *
 * Режимы (только development):
 *   - нормальный: события обрабатываются сразу
 *   - paused: события копятся в очереди до step() или resume()
 *
 * В production enqueue() вызывает fn() напрямую, без overhead.
 */

// ─── State ────────────────────────────────────────────────

export type QueuedEvent = {
  label: string
  data: unknown
  fn: () => void
}

let paused = false
const queue: QueuedEvent[] = []
const subscribers = new Set<() => void>()

function notify() {
  subscribers.forEach((cb) => cb())
}

// ─── Public API ───────────────────────────────────────────

export const devEventQueue = {
  get isPaused() {
    return paused
  },

  get events(): readonly QueuedEvent[] {
    return queue
  },

  /** Подписаться на изменения состояния (для React re-render) */
  subscribe(cb: () => void): () => void {
    subscribers.add(cb)
    return () => subscribers.delete(cb)
  },

  /** Поставить на паузу — все последующие события накапливаются */
  pause() {
    paused = true
    notify()
    console.log('%c[devQueue] ⏸ paused', 'color:#f59e0b;font-weight:bold')
  },

  /** Обработать одно событие из очереди */
  step() {
    const item = queue.shift()
    if (!item) return
    console.log(
      '%c[devQueue] ⏭ step →',
      'color:#06b6d4;font-weight:bold',
      item.label,
      item.data
    )
    item.fn()
    notify()
  },

  /** Снять паузу и обработать все накопленные события */
  resume() {
    paused = false
    const pending = queue.splice(0)
    notify()
    console.log(
      `%c[devQueue] ▶▶ resume (${pending.length} events)`,
      'color:#4ade80;font-weight:bold'
    )
    pending.forEach((item) => item.fn())
  },

  /**
   * Направить событие через очередь.
   * В production — вызывает fn() напрямую.
   * В dev — если paused, копит в очередь; иначе вызывает fn() сразу.
   */
  enqueue(label: string, data: unknown, fn: () => void) {
    if (process.env.NODE_ENV !== 'development') {
      fn()
      return
    }

    if (paused) {
      console.log('%c[devQueue] ⏸ queued →', 'color:#f59e0b', label, data)
      queue.push({ label, data, fn })
      notify()
      return
    }

    fn()
  },
}
